<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import WidgetAppearanceAPI from 'dashboard/api/widgetAppearance';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ChatBannerEditor from '../components/ChatBannerEditor.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const banners = ref([]);
const isLoading = ref(true);
const savingBannerKey = ref(null);
const deletingImageBannerId = ref(null);
const launcherIconUrl = ref(props.inbox.launcher_icon_url || '');
const launcherFileInput = ref(null);
const isUploadingLauncher = ref(false);
let nextDraftId = 1;

const showError = error => {
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    t('INBOX_MGMT.WIDGET_APPEARANCE.ERROR');
  useAlert(typeof message === 'string' ? message : JSON.stringify(message));
};

const fetchBanners = async () => {
  isLoading.value = true;
  try {
    const { data } = await WidgetAppearanceAPI.getBanners(props.inbox.id);
    banners.value = data;
  } catch (error) {
    showError(error);
  } finally {
    isLoading.value = false;
  }
};

const addBanner = () => {
  banners.value.push({
    draftId: nextDraftId,
    content: '',
    html_content: '',
    background_color: '#EFFFE8',
    text_color: '#16720C',
    image_url: '',
    link_url: '',
    link_target: '_blank',
    enabled: true,
    position: banners.value.length,
  });
  nextDraftId += 1;
};

const bannerKey = banner => banner.id || `draft-${banner.draftId}`;

const buildBannerFormData = values => {
  const formData = new FormData();
  const fields = {
    content: values.content,
    html_content: values.htmlContent,
    background_color: values.backgroundColor,
    text_color: values.textColor,
    link_url: values.linkUrl,
    link_target: values.linkTarget,
    enabled: values.enabled,
  };

  Object.entries(fields).forEach(([key, value]) => {
    formData.append(`chat_banner[${key}]`, value);
  });
  if (values.imageFile) {
    formData.append('chat_banner[image]', values.imageFile);
  }
  return formData;
};

const saveBanner = async ({ banner, values }) => {
  const key = bannerKey(banner);
  savingBannerKey.value = key;
  try {
    const formData = buildBannerFormData(values);
    const response = banner.id
      ? await WidgetAppearanceAPI.updateBanner(
          props.inbox.id,
          banner.id,
          formData
        )
      : await WidgetAppearanceAPI.createBanner(props.inbox.id, formData);
    const index = banners.value.findIndex(item => bannerKey(item) === key);
    banners.value.splice(index, 1, response.data);
    useAlert(t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.SAVE_SUCCESS'));
  } catch (error) {
    showError(error);
  } finally {
    savingBannerKey.value = null;
  }
};

const deleteBanner = async banner => {
  if (!banner.id) {
    banners.value = banners.value.filter(item => item !== banner);
    return;
  }
  // eslint-disable-next-line no-alert
  const isConfirmed = window.confirm(
    t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.DELETE_CONFIRM')
  );
  if (!isConfirmed) {
    return;
  }

  try {
    await WidgetAppearanceAPI.deleteBanner(props.inbox.id, banner.id);
    banners.value = banners.value.filter(item => item.id !== banner.id);
    useAlert(t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.DELETE_SUCCESS'));
  } catch (error) {
    showError(error);
  }
};

const deleteBannerImage = async banner => {
  if (!banner.id) return;

  deletingImageBannerId.value = banner.id;
  try {
    const { data } = await WidgetAppearanceAPI.deleteBannerImage(
      props.inbox.id,
      banner.id
    );
    const index = banners.value.findIndex(item => item.id === banner.id);
    banners.value.splice(index, 1, data);
  } catch (error) {
    showError(error);
  } finally {
    deletingImageBannerId.value = null;
  }
};

const moveBanner = async (index, direction) => {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= banners.value.length) return;

  const updated = [...banners.value];
  [updated[index], updated[nextIndex]] = [updated[nextIndex], updated[index]];
  banners.value = updated;

  const persistedBanners = updated.filter(banner => banner.id);
  if (persistedBanners.length < 2) return;

  try {
    const { data } = await WidgetAppearanceAPI.reorderBanners(
      props.inbox.id,
      persistedBanners.map(banner => banner.id)
    );
    const drafts = banners.value.filter(banner => !banner.id);
    banners.value = [...data, ...drafts];
  } catch (error) {
    await fetchBanners();
    showError(error);
  }
};

const openLauncherFilePicker = () => launcherFileInput.value?.click();

const uploadLauncherIcon = async event => {
  const [file] = event.target.files;
  if (!file) return;

  isUploadingLauncher.value = true;
  try {
    const { data } = await WidgetAppearanceAPI.updateLauncherIcon(
      props.inbox.id,
      file
    );
    launcherIconUrl.value = data.launcher_icon_url;
    useAlert(t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.SAVE_SUCCESS'));
  } catch (error) {
    showError(error);
  } finally {
    isUploadingLauncher.value = false;
    event.target.value = '';
  }
};

const deleteLauncherIcon = async () => {
  try {
    await WidgetAppearanceAPI.deleteLauncherIcon(props.inbox.id);
    launcherIconUrl.value = '';
    useAlert(t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.DELETE_SUCCESS'));
  } catch (error) {
    showError(error);
  }
};

onMounted(fetchBanners);
</script>

<template>
  <div class="mx-6 flex max-w-6xl flex-col gap-8 pb-12">
    <header class="flex flex-col gap-1">
      <h2 class="m-0 text-xl font-semibold text-n-slate-12">
        {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.TITLE') }}
      </h2>
      <p class="m-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.DESCRIPTION') }}
      </p>
    </header>

    <section
      class="grid gap-6 rounded-xl border border-n-weak bg-n-surface-1 p-6 lg:grid-cols-[minmax(0,1fr)_18rem]"
    >
      <div class="flex flex-col gap-2">
        <h3 class="m-0 text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.TITLE') }}
        </h3>
        <p class="m-0 max-w-2xl text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.DESCRIPTION') }}
        </p>
        <input
          ref="launcherFileInput"
          type="file"
          class="hidden"
          accept="image/jpeg,image/png,image/gif,image/webp"
          @change="uploadLauncherIcon"
        />
        <div class="mt-3 flex flex-wrap items-center gap-2">
          <Button
            icon="i-lucide-upload"
            :label="
              launcherIconUrl
                ? $t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.REPLACE')
                : $t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.UPLOAD')
            "
            :is-loading="isUploadingLauncher"
            @click="openLauncherFilePicker"
          />
          <Button
            v-if="launcherIconUrl"
            color="ruby"
            variant="ghost"
            :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.REMOVE')"
            @click="deleteLauncherIcon"
          />
        </div>
        <p class="m-0 mt-1 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.IMAGE_REQUIREMENTS') }}
        </p>
      </div>

      <div class="flex flex-col items-center justify-center gap-3">
        <div
          class="flex size-20 items-center justify-center overflow-hidden rounded-full bg-n-brand shadow-lg"
        >
          <img
            v-if="launcherIconUrl"
            :src="launcherIconUrl"
            :alt="$t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.PREVIEW')"
            class="size-full object-cover"
          />
          <span v-else class="i-lucide-message-circle size-8 text-white" />
        </div>
        <span class="text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.LAUNCHER.PREVIEW') }}
        </span>
      </div>
    </section>

    <section class="flex flex-col gap-4">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h3 class="m-0 text-base font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.TITLE') }}
          </h3>
          <p class="m-0 mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.DESCRIPTION') }}
          </p>
        </div>
        <Button
          icon="i-lucide-plus"
          :label="$t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.ADD')"
          @click="addBanner"
        />
      </div>

      <div v-if="isLoading" class="flex min-h-40 items-center justify-center">
        <Spinner :size="28" class="text-n-blue-9" />
      </div>

      <div
        v-else-if="!banners.length"
        class="flex min-h-40 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-strong bg-n-alpha-1 p-8 text-center"
      >
        <span class="i-lucide-gallery-horizontal-end size-8 text-n-slate-10" />
        <div>
          <p class="m-0 font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.EMPTY_TITLE') }}
          </p>
          <p class="m-0 mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.WIDGET_APPEARANCE.BANNER.EMPTY_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div v-else class="flex flex-col gap-4">
        <ChatBannerEditor
          v-for="(banner, index) in banners"
          :key="bannerKey(banner)"
          :banner="banner"
          :index="index"
          :total="banners.length"
          :is-saving="savingBannerKey === bannerKey(banner)"
          :is-deleting-image="deletingImageBannerId === banner.id"
          @save="saveBanner"
          @delete="deleteBanner"
          @delete-image="deleteBannerImage"
          @move="moveBanner"
        />
      </div>
    </section>
  </div>
</template>
